import {
    deepCopy,
    DisplayValue,
    DynamicTableColumn,
    Organization,
    OrgSecurityGroup,
    OrgFeature,
    parseEmbeddedTimestampsToFirebaseDate,
    TableAction,
} from '@newgenus/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SearchableSelectComponent } from '../../../searchable-select/searchable-select.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { OrgBoardService } from '../../services/org-board-service';
import { Component, inject, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { OrgDialogService } from '../../services/org-dialog.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRippleModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MaterialModule } from '../../../material.module';
import { DialogService } from '../../../../services/dialog.service';
import { NormalizePipe } from '../../format-pipe';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule, KeyValuePipe } from '@angular/common';
import { OrgSecurityGroupDetailsDialogComponent } from '../org-security-group-details-dialog/org-security-group-details-dialog';
import {
    OrgItemSelectorDialogComponent,
    ItemSelectorDialogResult,
} from '../org-item-selector-dialog/org-item-selector-dialog';

// Security Group Dialog — Create / Edit / List existing

@Component({
    selector: 'shared-org-security-group-dialog',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SearchableSelectComponent,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatTabsModule,
        MatTableModule,
        MatDialogModule,
        MatInputModule,
        MatRippleModule,
        MatSelectModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatChipsModule,
        MatBadgeModule,
        MaterialModule,
        NormalizePipe,
        KeyValuePipe,
    ],
    templateUrl: './org-security-group.html',
    styleUrls: ['./org-security-group.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgSecurityGroupComponent implements OnInit, OnDestroy {
    //#region Variables

    public form = new FormGroup({
        key: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null, Validators.compose([Validators.required, Validators.minLength(3)])),
        description: new FormControl<string | null>(null),
        features: new FormControl<string[] | null>(
            [],
            Validators.compose([Validators.required, Validators.minLength(1)])
        ),
        assigned: new FormControl<boolean | null>(false),
        assignedTo: new FormControl<string | null>(''),
    });

    public columns: DynamicTableColumn<OrgSecurityGroup>[] = [
        {
            columnDef: 'name',
            header: 'Name',
            cell: (element: OrgSecurityGroup) => element.name || '',
        },
        {
            columnDef: 'features',
            header: 'Features',
            cell: (element: OrgSecurityGroup) => {
                const keys = element.features || [];
                return keys.map((f) => element.featureDetails?.[f]?.name || f).join(', ');
            },
        },
        {
            columnDef: 'modifiedOn',
            header: 'Last Updated',
            cell: (element: OrgSecurityGroup) => {
                const date = element.modifiedOn;
                return date && date.toDate ? date.toDate().toDateString() : '';
            },
        },
        {
            columnDef: 'actions',
            header: '',
            cell: () => '',
        },
    ];
    public columnActions: TableAction<OrgSecurityGroup>[] = [
        {
            display: 'Edit',
            action: (row) => this.editSecurityGroup(row),
            color: 'primary',
            disabled: false,
        },
        {
            display: 'Delete',
            action: (row) => this.deleteSecurityGroup(row),
            color: 'warn',
            disabled: false,
        },
    ];

    public searchableFeatures: DisplayValue[] = [];
    public dataSource = new MatTableDataSource<OrgSecurityGroup>([]);
    public displayedColumns = this.columns.map((c) => c.columnDef);
    public selectedFeatures: Record<string, { key: string; name: string; permissions: string[] }> = {};
    public tabIndex = 0;
    public isProcessing = false;
    public isEditMode = false;
    public editingGroupName = '';

    /** Minimum spinner display time to avoid flashing */
    private readonly MIN_SPINNER_MS = 1200;

    /** Cleanup subject */
    private destroy$ = new Subject<void>();

    /** Tracks whether we're waiting for backend to process a save */
    private pendingSaveName: string | null = null;

    //#endregion

    private cdr = inject(ChangeDetectorRef);
    private dialogService = inject(DialogService);
    private matDialog = inject(MatDialog);
    public snackbar = inject(MatSnackBar);
    public obService = inject(OrgBoardService);
    public orgDialogService = inject(OrgDialogService);

    constructor(
        public dialogRef: MatDialogRef<OrgSecurityGroupComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organization: Organization;
            goToNewTab: boolean;
        }
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160); phone-scoped in
        // global-styles.scss, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    //#region Instantiation

    public ngOnInit(): void {
        this.loadSecurityGroupsTable();
        this.loadSearchableFeatures();
        this.subscribeToOrgUpdates();

        if (!this.data.goToNewTab) {
            this.tabIndex = 1;
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Subscribe to live organization updates so the dialog refreshes when backend tasks complete */
    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            this.loadSecurityGroupsTable();
            this.loadSearchableFeatures();

            // If we were waiting for a save to be processed, show the success toast now
            if (this.pendingSaveName) {
                const name = this.pendingSaveName;
                this.pendingSaveName = null;
                this.snackbar.open(
                    this.isEditMode
                        ? `Security Group "${name}" updated — data refreshed`
                        : `Security Group "${name}" created — data refreshed`,
                    'OK',
                    { duration: 3000 }
                );
                this.resetForm();
                this.stopProcessing();
            }

            this.cdr.detectChanges();
        });
    }

    private loadSecurityGroupsTable(): void {
        const keys: string[] = this.data.organization?.orgSecurityGroups?.securityGroupsKeys || [];
        const tableData: OrgSecurityGroup[] = [];
        if (keys)
            keys.forEach((key) => {
                if (this.data.organization.orgSecurityGroups[key]) {
                    tableData.push(
                        parseEmbeddedTimestampsToFirebaseDate(deepCopy(this.data.organization.orgSecurityGroups[key]))
                    );
                }
            });
        this.dataSource.data = tableData;

        // Custom filter predicate: search across name, features, hats, and users
        const org = this.data.organization;
        this.dataSource.filterPredicate = (sg: OrgSecurityGroup, filter: string) => {
            if (!filter) return true;
            const term = filter.toLowerCase();

            // Match on SG name
            if ((sg.name || '').toLowerCase().includes(term)) return true;

            // Match on feature names
            const featureKeys = sg.features || [];
            const orgFeatures = (org as any)?.orgFeatures || {};
            if (
                featureKeys.some((fk: string) => ((orgFeatures[fk]?.name || '') as string).toLowerCase().includes(term))
            )
                return true;

            // Match on hat names and user names (traverse hats that reference this SG)
            const orgHats = org?.orgHats;
            if (orgHats?.hatsKeys) {
                for (const hatKey of orgHats.hatsKeys) {
                    const hat = orgHats[hatKey] as any;
                    if (!hat) continue;
                    const sgArr: any[] = hat.securityGroupsArray || [];
                    const hasSg = sgArr.some((s: any) => (typeof s === 'string' ? s : s?.key) === sg.key);
                    if (!hasSg) continue;
                    if ((hat.name || '').toLowerCase().includes(term)) return true;
                    const userKeys: string[] = hat.assignedToArray || hat.userKeys || [];
                    const orgUsers = org?.orgUsers;
                    if (
                        orgUsers &&
                        userKeys.some((uk) =>
                            ((orgUsers[uk]?.name || orgUsers[uk]?.userName || '') as string)
                                .toLowerCase()
                                .includes(term)
                        )
                    )
                        return true;
                }
            }

            return false;
        };
    }

    private loadSearchableFeatures(): void {
        const featureKeys = this.data.organization?.orgFeatures?.featuresKeys || [];
        this.searchableFeatures = featureKeys
            .filter(
                (key) =>
                    this.data.organization.orgFeatures[key] && !this.data.organization.orgFeatures[key]?.systemFeature
            )
            .map((key) => ({
                display: this.data.organization.orgFeatures[key].name,
                value: key,
            }));
    }

    //#endregion
    //#region User Interactions

    public async saveSecurityGroup(): Promise<void> {
        if (this.form.invalid || this.isProcessing) return;

        this.startProcessing();
        const formValue = this.form.value;

        try {
            const securityGroupLike = {
                ...formValue,
                featureDetails: this.selectedFeatures as any,
                orgPosition: {} as any,
                policies: [] as any,
            } as OrgSecurityGroup;

            const saveResult = await this.obService.saveSecurityGroup(securityGroupLike);

            if (saveResult.succeeded) {
                // Don't show final toast yet — wait for org$ to emit with refreshed data
                this.pendingSaveName = formValue.name || '';
                this.snackbar.open('Saving... waiting for backend to process', undefined, { duration: 8000 });
                // Set a safety timeout in case the org$ update never arrives
                setTimeout(() => {
                    if (this.pendingSaveName) {
                        this.pendingSaveName = null;
                        this.snackbar.open(
                            this.isEditMode
                                ? `Security Group "${formValue.name}" saved (refresh may be delayed)`
                                : `Security Group "${formValue.name}" created (refresh may be delayed)`,
                            'OK',
                            { duration: 4000 }
                        );
                        this.resetForm();
                        this.loadSecurityGroupsTable();
                        this.stopProcessing();
                    }
                }, 15000);
                return; // Don't stop processing here — org$ or timeout will handle it
            }
        } catch (err) {
            this.snackbar.open('Failed to save security group. Please try again.', 'Dismiss', { duration: 5000 });
        } finally {
            if (!this.pendingSaveName) {
                this.stopProcessing();
            }
        }
    }

    public editSecurityGroup(row: OrgSecurityGroup): void {
        this.selectedFeatures = {};
        // Only include features that still exist in the organization
        const featureDetails = { ...row.featureDetails };
        Object.keys(featureDetails).forEach((key) => {
            if (this.data.organization.orgFeatures?.[key]) {
                this.selectedFeatures[key] = featureDetails[key];
            }
        });

        const parsed = {
            ...row,
            modifiedOn: row.modifiedOn?.toDate ? row.modifiedOn.toDate() : row.modifiedOn,
        };

        this.form.reset();
        this.form.patchValue(parsed);
        // Filter out orphaned feature keys that no longer exist in orgFeatures
        const validFeatures = (row.features || []).filter((k) => this.data.organization.orgFeatures?.[k]);
        this.featuresControl.setValue(validFeatures);

        // Sync selectedFeatures with the features array to remove stale featureDetails entries.
        // Without this, featureDetails keys that were removed from features[] persist in selectedFeatures
        // and get saved back, causing features vs featureDetails desync.
        this.onSelectFeature(validFeatures);

        this.isEditMode = true;
        this.editingGroupName = row.name || '';
        this.tabIndex = 0;
    }

    public async deleteSecurityGroup(row: OrgSecurityGroup): Promise<void> {
        const confirmed = await this.dialogService.asyncConfirm(
            'Delete Security Group',
            `Are you sure you want to delete the security group "${row.name}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        this.startProcessing();
        try {
            const sgData = { ...row, isSoftDeleted: true } as OrgSecurityGroup;
            const result = await this.obService.saveSecurityGroup(sgData);
            if (result.succeeded) {
                this.snackbar.open(`Security Group "${row.name}" deleted`, 'OK', { duration: 3000 });
                this.dataSource.data = this.dataSource.data.filter((sg) => sg.key !== row.key);
            }
        } catch (err) {
            this.snackbar.open('Failed to delete security group. Please try again.', 'Dismiss', { duration: 5000 });
        } finally {
            this.stopProcessing();
        }
    }

    public resetForm(): void {
        this.form.reset({ assigned: false, assignedTo: '', features: [] });
        this.selectedFeatures = {};
        this.isEditMode = false;
        this.editingGroupName = '';
    }

    public applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value || '';
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    /** Opens the Security Group Explorer dialog */
    public openSGExplorer(): void {
        import('../org-security-group-explorer-dialog/org-security-group-explorer-dialog').then((m) => {
            this.matDialog.open(m.OrgSecurityGroupExplorerDialogComponent, {
                width: '90%',
                height: '90vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                data: {
                    organization: this.data.organization,
                },
            });
        });
    }

    /** Opens the feature selector dialog (Browse button) */
    public openFeatureSelector(): void {
        const dialogRef = this.matDialog.open(OrgItemSelectorDialogComponent, {
            width: '70vw',
            height: '80vh',
            maxWidth: '900px',
            maxHeight: '800px',
            panelClass: 'item-selector-panel',
            data: {
                organization: this.data.organization,
                type: 'features',
                selectedKeys: this.featuresControl.value || [],
                permissionSelections: this.getExistingPermissionSelections(),
            },
        });

        dialogRef.afterClosed().subscribe((result: ItemSelectorDialogResult | null) => {
            if (!result) return;
            this.featuresControl.setValue(result.selectedKeys);
            this.onSelectFeature(result.selectedKeys);

            // Apply permission selections from the browse dialog
            if (result.permissionSelections) {
                for (const [featureKey, perms] of Object.entries(result.permissionSelections)) {
                    if (this.selectedFeatures[featureKey]) {
                        this.selectedFeatures[featureKey].permissions = perms;
                    }
                }
            }

            this.form.markAsDirty();
            this.cdr.detectChanges();
        });
    }

    /** Opens the Security Group Details dialog (read-only) for a group */
    public viewSecurityGroupDetails(sgKey: string): void {
        const sg = this.data.organization?.orgSecurityGroups?.[sgKey];
        if (!sg) {
            this.snackbar.open('Security group not found', 'Dismiss', { duration: 3000 });
            return;
        }

        this.matDialog.open(OrgSecurityGroupDetailsDialogComponent, {
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            data: {
                securityGroup: sg,
                securityGroupKey: sgKey,
                organization: this.data.organization,
            },
            panelClass: 'security-group-details-dialog',
        });
    }

    //#endregion
    //#region Events and Domain Functions

    public onSelectFeature(currentSelection: string[]): void {
        // Add or update any that are selected.
        currentSelection?.forEach((f) => {
            this.selectedFeatures[f] = {
                name: f,
                key: f,
                permissions: this.selectedFeatures[f]?.permissions || [],
            };
        });

        // Remove any that are unselected.
        Object.keys(this.selectedFeatures).forEach((f) => {
            if (!currentSelection.includes(f)) {
                delete this.selectedFeatures[f];
            }
        });
    }

    /** Remove feature chip from selected features and update form */
    public removeFeature(featureKey: string): void {
        const current: string[] = this.featuresControl.value || [];
        this.featuresControl.setValue(current.filter((k) => k !== featureKey));
        delete this.selectedFeatures[featureKey];
        this.form.markAsDirty();
    }

    /** Returns display items for currently selected features */
    public getSelectedFeatureItems(): DisplayValue[] {
        const selected: string[] = this.featuresControl.value || [];
        if (!selected.length) return [];
        return selected
            .map((val) => this.searchableFeatures.find((item) => item.value === val))
            .filter((item): item is DisplayValue => !!item);
    }

    /** Builds a map of existing permission selections to pass into the Browse dialog */
    private getExistingPermissionSelections(): Record<string, string[]> {
        const selections: Record<string, string[]> = {};
        for (const [key, feat] of Object.entries(this.selectedFeatures)) {
            if (feat.permissions && feat.permissions.length > 0) {
                selections[key] = [...feat.permissions];
            }
        }
        return selections;
    }

    //#endregion
    //#region Getters and Filters

    public get features(): Record<string, OrgFeature> {
        return this.data.organization.orgFeatures as unknown as Record<string, OrgFeature>;
    }

    public get columnsWithoutActions(): DynamicTableColumn[] {
        return this.columns.filter((c) => c.columnDef !== 'actions');
    }

    public get featuresControl(): FormControl {
        return this.form.get('features') as FormControl;
    }

    public get selectedFeaturesWithoutSystemFeatures(): Record<
        string,
        { key: string; name: string; permissions: string[] }
    > {
        const keys = Object.keys(this.selectedFeatures).filter(
            (k) => this.features[k] && !this.features[k]?.systemFeature
        );
        const newForm: Record<string, { key: string; name: string; permissions: string[] }> = {};
        keys.forEach((k) => (newForm[k] = this.selectedFeatures[k]));
        return newForm;
    }

    /** Dynamic dialog title */
    public get dialogTitle(): string {
        return this.isEditMode ? 'Edit Security Group' : 'New Security Group';
    }

    /** Subtitle showing what's being edited */
    public get dialogSubtitle(): string {
        return this.isEditMode ? this.editingGroupName : '';
    }

    /** Quick stats for the form */
    public get formStats(): { features: number; permissions: number } {
        const featKeys = this.featuresControl.value || [];
        const totalPermissions = Object.values(this.selectedFeatures).reduce(
            (sum, f) => sum + (f.permissions?.length || 0),
            0
        );
        return {
            features: featKeys.length,
            permissions: totalPermissions,
        };
    }

    //#endregion
    //#region Processing State

    private processingStartTime = 0;

    private startProcessing(): void {
        this.isProcessing = true;
        this.processingStartTime = Date.now();
    }

    private async stopProcessing(): Promise<void> {
        const elapsed = Date.now() - this.processingStartTime;
        if (elapsed < this.MIN_SPINNER_MS) {
            await new Promise((resolve) => setTimeout(resolve, this.MIN_SPINNER_MS - elapsed));
        }
        this.isProcessing = false;
    }

    //#endregion
}
